import React from "react";
import { motion } from "framer-motion";
import { Progress } from "@/components/ui/progress";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const genderData = [
  { name: "Female", value: 62, color: "#FF3B30" },
  { name: "Male", value: 35, color: "#007AFF" },
  { name: "Other", value: 3, color: "#737373" },
];

const ageData = [
  { range: "18-24", percentage: 35 },
  { range: "25-34", percentage: 42 },
  { range: "35-44", percentage: 15 },
  { range: "45+", percentage: 8 },
];

const topCities = [
  { city: "Los Angeles", percentage: 18 },
  { city: "New York", percentage: 14 },
  { city: "Miami", percentage: 11 },
  { city: "London", percentage: 9 },
  { city: "Dubai", percentage: 7 },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function AudienceDemographics() {
  return (
    <section
      id="audience-demographics"
      data-testid="audience-demographics-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#121212] relative"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
            Audience Insights
          </p>
          <h2
            data-testid="demographics-heading"
            className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white"
          >
            Know Who You're Reaching
          </h2>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {/* Gender Split */}
          <motion.div
            variants={itemVariants}
            data-testid="gender-chart"
            className="bg-[#1A1A1A] border border-white/10 rounded-xl p-8"
          >
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-6">
              Gender Split
            </h3>
            <div className="h-48 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={genderData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                    stroke="none"
                  >
                    {genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "#1A1A1A",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "8px",
                      color: "#fff",
                    }}
                    formatter={(value) => [`${value}%`, ""]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col gap-3">
              {genderData.map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-white/60">{item.name}</span>
                  </div>
                  <span className="text-sm font-bold text-white font-['JetBrains_Mono']">
                    {item.value}%
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Age Distribution */}
          <motion.div
            variants={itemVariants}
            data-testid="age-distribution"
            className="bg-[#1A1A1A] border border-white/10 rounded-xl p-8"
          >
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-6">
              Age Distribution
            </h3>
            <div className="flex flex-col gap-6">
              {ageData.map((item) => (
                <div key={item.range}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-white/60">{item.range}</span>
                    <span className="text-sm font-bold text-white font-['JetBrains_Mono']">
                      {item.percentage}%
                    </span>
                  </div>
                  <Progress
                    value={item.percentage}
                    className="h-2 bg-white/5 [&>div]:bg-[#FF3B30]"
                  />
                </div>
              ))}
            </div>
          </motion.div>

          {/* Top Cities */}
          <motion.div
            variants={itemVariants}
            data-testid="top-cities"
            className="bg-[#1A1A1A] border border-white/10 rounded-xl p-8"
          >
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-6">
              Top Cities
            </h3>
            <div className="flex flex-col gap-5">
              {topCities.map((item, i) => (
                <div key={item.city}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-[#FF3B30] font-['JetBrains_Mono'] w-5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm text-white/80">{item.city}</span>
                    </div>
                    <span className="text-sm font-bold text-white font-['JetBrains_Mono']">
                      {item.percentage}%
                    </span>
                  </div>
                  <Progress
                    value={item.percentage}
                    className="h-1.5 bg-white/5 [&>div]:bg-[#007AFF]"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
